import React, { useContext, useState, useEffect } from "react";
import {
  Paper,
  Container,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Chip,
  LinearProgress,
  Fade,
  Grow,
  useTheme,
} from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";

// Icons
import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import TimerIcon from "@material-ui/icons/Timer";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";

import { toast } from "react-toastify";
import { isArray, isEmpty } from "lodash";
import moment from "moment";

import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import useDashboard from "../../hooks/useDashboard";
import useContacts from "../../hooks/useContacts";
import { ChatsUser } from "./ChartsUser";
import { ChartsDate } from "./ChartsDate";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minHeight: "calc(100vh - 64px)",
  },
  // Header Section
  headerSection: {
    marginBottom: theme.spacing(4),
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "1.75rem",
    color: theme.palette.textPrimary,
    marginBottom: theme.spacing(0.5),
  },
  headerSubtitle: {
    color: theme.palette.textSecondary,
    fontSize: "0.9rem",
  },
  // Stat Cards
  statCard: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 16,
    padding: theme.spacing(2.5),
    color: "#FFFFFF",
    height: 150,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.3)",
    },
  },
  statCardPrimary: {
    background: theme.palette.cardPrimary || "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
  },
  statCardSecondary: {
    background: theme.palette.cardSecondary || "linear-gradient(135deg, #14B8A6 0%, #06B6D4 100%)",
  },
  statCardSuccess: {
    background: theme.palette.cardSuccess || "linear-gradient(135deg, #22C55E 0%, #10B981 100%)",
  },
  statCardWarning: {
    background: theme.palette.cardWarning || "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
  },
  statCardInfo: {
    background: theme.palette.cardInfo || "linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)",
  },
  statCardPurple: {
    background: theme.palette.cardPurple || "linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)",
  },
  statCardIcon: {
    position: "absolute",
    right: -10,
    bottom: -10,
    fontSize: 100,
    opacity: 0.15,
    transform: "rotate(-15deg)",
  },
  statCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  statCardTitle: {
    fontSize: "0.85rem",
    fontWeight: 500,
    opacity: 0.9,
    letterSpacing: "0.025em",
  },
  statCardValue: {
    fontSize: "2.25rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  statIconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(10px)",
  },
  // Filters Section
  filtersCard: {
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.borderPrimary}`,
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  filtersTitle: {
    fontWeight: 600,
    color: theme.palette.textPrimary,
  },
  filterChip: {
    borderRadius: 8,
    fontWeight: 500,
  },
  // Charts Section
  chartCard: {
    borderRadius: 16,
    padding: theme.spacing(3),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.borderPrimary}`,
    marginBottom: theme.spacing(3),
  },
  chartHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  chartTitle: {
    fontWeight: 600,
    color: theme.palette.textPrimary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  // Table Section
  tableCard: {
    borderRadius: 16,
    overflow: "hidden",
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.borderPrimary}`,
    marginBottom: theme.spacing(3),
  },
  tableHeader: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.borderPrimary}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  // Loading
  loadingBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    borderRadius: "16px 16px 0 0",
  },
  // Form Controls
  selectField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      background: theme.palette.background.default,
    },
  },
  dateField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      background: theme.palette.background.default,
    },
  },
  filterButton: {
    borderRadius: 10,
    padding: theme.spacing(1.25, 3),
    fontWeight: 600,
    textTransform: "none",
  },
  // Decorative elements
  decorativeCircle: {
    position: "absolute",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.1)",
  },
}));

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, gradient, delay }) => {
  const classes = useStyles();
  
  return (
    <Grow in timeout={600 + delay}>
      <Paper className={`${classes.statCard} ${gradient}`} elevation={0}>
        {/* Decorative circles */}
        <Box 
          className={classes.decorativeCircle} 
          style={{ width: 80, height: 80, top: -20, right: 40, opacity: 0.1 }}
        />
        <Box 
          className={classes.decorativeCircle} 
          style={{ width: 40, height: 40, top: 20, right: 10, opacity: 0.15 }}
        />
        
        <Box className={classes.statCardHeader}>
          <Typography className={classes.statCardTitle}>
            {title}
          </Typography>
          <Box className={classes.statIconWrapper}>
            <Icon style={{ fontSize: 24 }} />
          </Box>
        </Box>
        
        <Box>
          <Typography className={classes.statCardValue}>
            {value || "0"}
          </Typography>
        </Box>
        
        <Icon className={classes.statCardIcon} />
      </Paper>
    </Grow>
  );
};

const Dashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [period, setPeriod] = useState(0);
  const [filterType, setFilterType] = useState(1);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const { find } = useDashboard();

  useEffect(() => {
    async function firstLoad() {
      await fetchData();
    }
    setTimeout(() => {
      firstLoad();
    }, 1000);
  }, []);

  async function handleChangePeriod(value) {
    setPeriod(value);
  }

  async function handleChangeFilterType(value) {
    setFilterType(value);
    if (value === 1) {
      setPeriod(0);
    } else {
      setDateFrom("");
      setDateTo("");
    }
  }

  async function fetchData() {
    setLoading(true);

    let params = {};

    if (period > 0) {
      params = { days: period };
    }

    if (!isEmpty(dateFrom) && moment(dateFrom).isValid()) {
      params = { ...params, date_from: moment(dateFrom).format("YYYY-MM-DD") };
    }

    if (!isEmpty(dateTo) && moment(dateTo).isValid()) {
      params = { ...params, date_to: moment(dateTo).format("YYYY-MM-DD") };
    }

    if (Object.keys(params).length === 0) {
      toast.error(i18n.t("dashboard.toasts.selectFilterError"));
      setLoading(false);
      return;
    }

    const data = await find(params);

    setCounters(data.counters);
    if (isArray(data.attendants)) {
      setAttendants(data.attendants);
    } else {
      setAttendants([]);
    }

    setLoading(false);
  }

  function formatTime(minutes) {
    return moment()
      .startOf("day")
      .add(minutes, "minutes")
      .format("HH[h] mm[m]");
  }

  const GetContacts = (all) => {
    let props = {};
    if (all) {
      props = {};
    }
    const { count } = useContacts(props);
    return count;
  };

  return (
    <Box style={{ background: theme.palette.fancyBackground, minHeight: "100vh" }}>
      <Container maxWidth="xl" className={classes.container}>
        {/* Header Section */}
        <Fade in timeout={400}>
          <Box className={classes.headerSection}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography className={classes.headerTitle}>
                  📊 Dashboard
                </Typography>
                <Typography className={classes.headerSubtitle}>
                  {i18n.t("dashboard.subtitle") || "Acompanhe as métricas e desempenho do seu atendimento"}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => fetchData()} 
                disabled={loading}
                style={{ 
                  background: theme.palette.primary.main, 
                  color: "#fff",
                  borderRadius: 12,
                }}
              >
                <RefreshIcon className={loading ? "rotating" : ""} />
              </IconButton>
            </Box>
          </Box>
        </Fade>

        {/* Stats Cards */}
        <Grid container spacing={2} style={{ marginBottom: 32 }}>
          <Grid item xs={6} sm={6} md={4} lg={2}>
            <StatCard
              title={i18n.t("dashboard.counters.inTalk")}
              value={counters.supportHappening}
              icon={PhoneInTalkIcon}
              gradient={classes.statCardPrimary}
              delay={0}
            />
          </Grid>
          
          <Grid item xs={6} sm={6} md={4} lg={2}>
            <StatCard
              title={i18n.t("dashboard.counters.waiting")}
              value={counters.supportPending}
              icon={HourglassEmptyIcon}
              gradient={classes.statCardWarning}
              delay={50}
            />
          </Grid>
          
          <Grid item xs={6} sm={6} md={4} lg={2}>
            <StatCard
              title={i18n.t("dashboard.counters.finished")}
              value={counters.supportFinished}
              icon={CheckCircleOutlineIcon}
              gradient={classes.statCardSuccess}
              delay={100}
            />
          </Grid>
          
          <Grid item xs={6} sm={6} md={4} lg={2}>
            <StatCard
              title={i18n.t("dashboard.counters.newContacts")}
              value={GetContacts(true)}
              icon={PersonAddIcon}
              gradient={classes.statCardSecondary}
              delay={150}
            />
          </Grid>
          
          <Grid item xs={6} sm={6} md={4} lg={2}>
            <StatCard
              title={i18n.t("dashboard.counters.averageTalkTime")}
              value={formatTime(counters.avgSupportTime)}
              icon={AccessTimeIcon}
              gradient={classes.statCardInfo}
              delay={200}
            />
          </Grid>
          
          <Grid item xs={6} sm={6} md={4} lg={2}>
            <StatCard
              title={i18n.t("dashboard.counters.averageWaitTime")}
              value={formatTime(counters.avgWaitTime)}
              icon={TimerIcon}
              gradient={classes.statCardPurple}
              delay={250}
            />
          </Grid>
        </Grid>

        {/* Filters Section */}
        <Fade in timeout={600}>
          <Paper className={classes.filtersCard} elevation={0}>
            {loading && <LinearProgress className={classes.loadingBar} />}
            
            <Box className={classes.filtersHeader}>
              <FilterListIcon color="primary" />
              <Typography className={classes.filtersTitle}>
                {i18n.t("dashboard.filters.title") || "Filtros"}
              </Typography>
            </Box>
            
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined" size="small" className={classes.selectField}>
                  <InputLabel>{i18n.t("dashboard.filters.filterType.title")}</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => handleChangeFilterType(e.target.value)}
                    label={i18n.t("dashboard.filters.filterType.title")}
                  >
                    <MenuItem value={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarTodayIcon fontSize="small" />
                        {i18n.t("dashboard.filters.filterType.options.perDate")}
                      </Box>
                    </MenuItem>
                    <MenuItem value={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTimeIcon fontSize="small" />
                        {i18n.t("dashboard.filters.filterType.options.perPeriod")}
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {filterType === 1 ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label={i18n.t("dashboard.filters.initialDate")}
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.dateField}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label={i18n.t("dashboard.filters.finalDate")}
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.dateField}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth variant="outlined" size="small" className={classes.selectField}>
                    <InputLabel>{i18n.t("dashboard.periodSelect.title")}</InputLabel>
                    <Select
                      value={period}
                      onChange={(e) => handleChangePeriod(e.target.value)}
                      label={i18n.t("dashboard.periodSelect.title")}
                    >
                      <MenuItem value={0}>{i18n.t("dashboard.periodSelect.options.none")}</MenuItem>
                      <MenuItem value={3}>{i18n.t("dashboard.periodSelect.options.last3")}</MenuItem>
                      <MenuItem value={7}>{i18n.t("dashboard.periodSelect.options.last7")}</MenuItem>
                      <MenuItem value={15}>{i18n.t("dashboard.periodSelect.options.last15")}</MenuItem>
                      <MenuItem value={30}>{i18n.t("dashboard.periodSelect.options.last30")}</MenuItem>
                      <MenuItem value={60}>{i18n.t("dashboard.periodSelect.options.last60")}</MenuItem>
                      <MenuItem value={90}>{i18n.t("dashboard.periodSelect.options.last90")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <ButtonWithSpinner
                  loading={loading}
                  onClick={() => fetchData()}
                  variant="contained"
                  color="primary"
                  fullWidth
                  className={classes.filterButton}
                  startIcon={<FilterListIcon />}
                >
                  {i18n.t("dashboard.buttons.filter")}
                </ButtonWithSpinner>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Attendants Table */}
        {attendants.length > 0 && (
          <Fade in timeout={700}>
            <Paper className={classes.tableCard} elevation={0}>
              <Box className={classes.tableHeader}>
                <Typography className={classes.chartTitle}>
                  👥 {i18n.t("dashboard.attendants.title") || "Atendentes Online"}
                </Typography>
                <Chip 
                  label={`${attendants.length} online`} 
                  color="primary" 
                  size="small"
                  className={classes.filterChip}
                />
              </Box>
              <TableAttendantsStatus attendants={attendants} loading={loading} />
            </Paper>
          </Fade>
        )}

        {/* Charts Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Fade in timeout={800}>
              <Paper className={classes.chartCard} elevation={0}>
                <Box className={classes.chartHeader}>
                  <Typography className={classes.chartTitle}>
                    📈 {i18n.t("dashboard.charts.perUser") || "Atendimentos por Usuário"}
                  </Typography>
                </Box>
                <ChatsUser />
              </Paper>
            </Fade>
          </Grid>
          
          <Grid item xs={12} lg={6}>
            <Fade in timeout={900}>
              <Paper className={classes.chartCard} elevation={0}>
                <Box className={classes.chartHeader}>
                  <Typography className={classes.chartTitle}>
                    📊 {i18n.t("dashboard.charts.perDay") || "Atendimentos por Dia"}
                  </Typography>
                </Box>
                <ChartsDate />
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* CSS for rotation animation */}
      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;
